import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CLIENT_TYPES } from "@/lib/services";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";

export default function ClientDialog({ client, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", client_type: "", partner_director: false, notes: "" });
  const editing = Boolean(client?.id);

  useEffect(() => {
    if (client) setForm({ name: client.name || "", client_type: client.client_type || "", partner_director: Boolean(client.partner_director), notes: client.notes || "" });
  }, [client]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, client_type: form.client_type || null };
      const { data } = editing ? await api.patch(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success(editing ? "Client updated" : "Client added");
      onClose();
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(client)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit client" : "New client"}</DialogTitle>
          <DialogDescription>Clients are shared with the whole team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Client name</Label>
            <Input required value={form.name} onChange={set("name")} className={field} placeholder="e.g. Bristol Bakes Ltd" data-testid="client-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Client type</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="client-type-options">
              {CLIENT_TYPES.map((t) => (
                <button type="button" key={t.value} onClick={() => setForm((f) => ({ ...f, client_type: f.client_type === t.value ? "" : t.value, partner_director: t.value === "individual" ? f.partner_director : false }))}
                  data-testid={`client-type-${t.value}`}
                  className={`rounded-xl h-11 text-sm font-semibold border transition-colors duration-200 ${form.client_type === t.value ? "bg-[#103080] text-white border-[#103080]" : "border-[#103080]/20 text-[#103080] bg-white hover:border-[#0090D0]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {form.client_type === "individual" && (
              <label className="flex items-center gap-2 pt-1 text-sm text-[#103080] cursor-pointer">
                <input type="checkbox" checked={form.partner_director} onChange={(e) => setForm((f) => ({ ...f, partner_director: e.target.checked }))}
                  className="accent-[#103080] w-4 h-4" data-testid="client-partner-director-checkbox" />
                Also a Partner / Director
              </label>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Work notes</Label>
            <Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Work notes only, no personal details" className="rounded-xl bg-[#FDFBF7] border-[#103080]/20" data-testid="client-notes-input" />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" data-testid="client-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              {editing ? "Save changes" : "Add client"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
